import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Search, Users, Check, SlidersHorizontal, HelpCircle, EyeOff, CheckCircle, ChevronRight
} from 'lucide-react';
import { fetchPeerInboxRequest, updateSkillEvalRequest } from '../../../redux/actions/skillEvaluationActions';
import { fetchCampagnesRequest } from '../../../redux/actions/campagnesActions';
import { fetchJobSkillsRequest } from '../../../redux/actions/jobSkillActions';
import { fetchPeerConfig } from '../../../redux/actions/peerEvaluationConfigActions';

const NIVEAUX = [
    { value: 1, label: "Débutant" },
    { value: 2, label: "Intermédiaire" },
    { value: 3, label: "Avancé" },
    { value: 4, label: "Expert" }
];

export default function EvalPairs() {
    const dispatch = useDispatch();
    const { inboxPeers, loading } = useSelector(state => state.skillEvaluation || { inboxPeers: [], loading: false });
    const { list: campagnesList } = useSelector(state => state.campagnes || { list: [] });
    const { user } = useSelector(state => state.auth || {});
    const { jobSkills } = useSelector(state => state.jobSkill || { jobSkills: [] });
    const { config: peerConfig } = useSelector(state => state.peerEvaluationConfig || {});
    const isAnonymous = peerConfig?.isAnonymous ?? false;

    const [selectedPeerEval, setSelectedPeerEval] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCampagneId, setSelectedCampagneId] = useState("");
    const [peerEvals, setPeerEvals] = useState({});
    const [peerComments, setPeerComments] = useState({});

    useEffect(() => {
        dispatch(fetchPeerInboxRequest());
        dispatch(fetchCampagnesRequest());
        dispatch(fetchPeerConfig());
    }, [dispatch]);

    const activeCampagnes = useMemo(() => (campagnesList || []).filter(c => c.status === true), [campagnesList]);

    useEffect(() => {
        if (!selectedCampagneId && activeCampagnes.length > 0) {
            const defaultCamp = activeCampagnes.find(c => c.isDefault) || activeCampagnes[0];
            if (defaultCamp) setSelectedCampagneId(defaultCamp.id.toString());
        }
    }, [activeCampagnes, selectedCampagneId]);

    useEffect(() => {
        if (selectedPeerEval) {
            const evals = {};
            const comments = {};
            selectedPeerEval.niveaux?.forEach(n => {
                evals[n.skillId] = n.scoreObtenu || null;
                comments[n.skillId] = n.commentaire || "";
            });
            setPeerEvals(evals);
            setPeerComments(comments);
            if (selectedPeerEval.jobId) dispatch(fetchJobSkillsRequest(selectedPeerEval.jobId));
        }
    }, [selectedPeerEval, dispatch]);

    const handleEval = (compId, level) => setPeerEvals(p => ({ ...p, [compId]: level }));
    const handleComment = (compId, text) => setPeerComments(p => ({ ...p, [compId]: text }));

    const handleSave = () => {
        if (!selectedPeerEval) return;
        const niveauxArray = Object.entries(peerEvals).map(([skillId, score]) => ({
            skillId: parseInt(skillId),
            scoreCollab: score ? parseInt(score) : 1,
            scoreObtenu: score ? parseInt(score) : 1,
            commentaire: peerComments[skillId] || ""
        }));
        const updateDto = {
            campagneId: selectedPeerEval.campagneId,
            userId: selectedPeerEval.userId,
            statut: 'EN_ATTENTE_MANAGER',
            evaluateurId: user?.id || user?.uid,
            niveaux: niveauxArray
        };
        dispatch(updateSkillEvalRequest(selectedPeerEval.id, updateDto));
    };

    const filteredInbox = useMemo(() => {
        return (inboxPeers || []).filter(item => {
            const fullName = item.userFullName || "";
            const matchSearch = fullName.toLowerCase().includes(searchQuery.toLowerCase());
            const matchCamp = !selectedCampagneId || item.campagneId === Number(selectedCampagneId);
            return matchSearch && matchCamp;
        });
    }, [inboxPeers, searchQuery, selectedCampagneId]);

    const relevantSkills = useMemo(() => {
        return (jobSkills || []).filter(js => 
            js.modeEvaluationCode === "PAIRES" || 
            js.modeEvaluationNom?.toLowerCase().includes("paire") ||
            js.modeEvaluationNom?.toLowerCase().includes("peer") ||
            js.modeEvaluationNom?.toLowerCase().includes("360")
        );
    }, [jobSkills]);

    // Validation to enable save button
    const isFormComplete = relevantSkills.length > 0 && relevantSkills.every(s => peerEvals[s.skillId] !== undefined && peerEvals[s.skillId] !== null);

    return (
        <div style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>
            
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                <div>
                    <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: '0 0 4px 0' }}>Évaluation des Pairs</h1>
                    <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>Apportez votre vision (360°) sur les compétences de vos collègues.</p>
                </div>
                <select
                    style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', minWidth: '220px', outline: 'none' }}
                    value={selectedCampagneId}
                    onChange={e => setSelectedCampagneId(e.target.value)}
                >
                    <option value="">— Sélectionnez une campagne —</option>
                    {activeCampagnes.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '24px', alignItems: 'start' }}>
                
                {/* SIDEBAR */}
                <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
                    <div style={{ padding: '16px', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ position: 'relative' }}>
                            <Search size={14} style={{ position: 'absolute', left: '10px', top: '10px', color: '#94a3b8' }} />
                            <input 
                                type="text" 
                                placeholder="Chercher un collègue..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ width: '100%', padding: '8px 10px 8px 30px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px', outline: 'none' }}
                            />
                        </div>
                    </div>
                    <div style={{ overflowY: 'auto', padding: '8px', flex: 1 }}>
                        {loading && inboxPeers.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#64748b', fontSize: '13px' }}>Chargement...</div>
                        ) : filteredInbox.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                                <Users size={32} style={{ margin: '0 auto 12px' }} />
                                <div style={{ fontSize: '13px' }}>Aucun collègue à évaluer pour cette campagne.</div>
                            </div>
                        ) : filteredInbox.map(item => {
                            const isActive = selectedPeerEval?.id === item.id;
                            const isDone = item.statut === 'VALIDEE' || item.statut === 'EN_ATTENTE_MANAGER';
                            return (
                                <div 
                                    key={item.id} 
                                    onClick={() => setSelectedPeerEval(item)}
                                    style={{ padding: '12px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', borderRadius: '8px', background: isActive ? '#f8fafc' : 'transparent', border: '1px solid', borderColor: isActive ? '#e2e8f0' : 'transparent', marginBottom: '4px' }}
                                >
                                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: isDone ? '#ecfdf5' : '#e0f2fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold', color: isDone ? '#059669' : '#0369a1' }}>
                                        {item.userFullName?.[0] || '?'}
                                    </div>
                                    <div style={{ flex: 1, overflow: 'hidden' }}>
                                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{item.userFullName}</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: isDone ? '#10b981' : '#f59e0b', marginTop: '2px' }}>
                                            {isDone ? <CheckCircle size={12} /> : <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#f59e0b' }} />}
                                            {isDone ? 'Évalué' : 'À évaluer'}
                                        </div>
                                    </div>
                                    {isActive && <ChevronRight size={16} color="#94a3b8" />}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* MAIN CONTENT */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {!selectedPeerEval ? (
                        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', color: '#64748b' }}>
                            <HelpCircle size={48} color="#cbd5e1" style={{ marginBottom: '16px' }} />
                            <h3 style={{ fontSize: '18px', color: '#334155', margin: '0 0 8px 0' }}>Dossier 360°</h3>
                            <p style={{ fontSize: '14px', margin: 0 }}>Sélectionnez un collègue dans la liste pour commencer l'évaluation.</p>
                        </div>
                    ) : (
                        <>
                            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <div style={{ width: '50px', height: '50px', borderRadius: '12px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '800', color: '#475569' }}>
                                        {selectedPeerEval.userFullName?.[0] || '?'}
                                    </div>
                                    <div>
                                        <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: '0 0 4px 0' }}>{selectedPeerEval.userFullName}</h2>
                                        <div style={{ fontSize: '12px', color: '#64748b' }}>Évaluation • {selectedPeerEval.campagneNom}</div>
                                        {isAnonymous && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', fontSize: '11px', color: '#0284c7', fontWeight: 600, background: '#f0f9ff', padding: '4px 8px', borderRadius: '4px', width: 'fit-content' }}>
                                                <EyeOff size={12} /> Votre évaluation restera strictement anonyme.
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <button 
                                    onClick={handleSave} 
                                    disabled={loading || !isFormComplete}
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: isFormComplete ? '#10b981' : '#e2e8f0', color: isFormComplete ? '#fff' : '#94a3b8', border: 'none', borderRadius: '8px', cursor: isFormComplete ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: '600', transition: 'all 0.2s' }}
                                >
                                    <Check size={16} /> {loading ? 'Sauvegarde...' : 'Enregistrer mon avis'}
                                </button>
                            </div>

                            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                <div style={{ padding: '16px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600', color: '#334155' }}>
                                    <SlidersHorizontal size={18} color="#64748b" /> Compétences Collaboratives
                                </div>
                                
                                {relevantSkills.length === 0 ? (
                                    <div style={{ padding: '40px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
                                        Aucune compétence collaborative (360/Pair) n'est définie pour le poste de ce collaborateur.
                                    </div>
                                ) : (
                                    <div style={{ padding: '20px' }}>
                                        {relevantSkills.map((skill, idx) => {
                                            const isLast = idx === relevantSkills.length - 1;
                                            return (
                                                <div key={skill.skillId} style={{ paddingBottom: isLast ? '0' : '20px', marginBottom: isLast ? '0' : '20px', borderBottom: isLast ? 'none' : '1px solid #f1f5f9' }}>
                                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>{skill.skillNom}</div>
                                                    
                                                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(250px, 1fr) 1.5fr', gap: '24px' }}>
                                                        {/* Notation */}
                                                        <div>
                                                            <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Votre Note</div>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                                {NIVEAUX.map(niv => {
                                                                    const isSelected = peerEvals[skill.skillId] === niv.value;
                                                                    return (
                                                                        <button
                                                                            key={niv.value}
                                                                            onClick={() => handleEval(skill.skillId, niv.value)}
                                                                            style={{
                                                                                padding: '6px 12px', borderRadius: '20px', border: '1px solid', fontSize: '12px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                                                                                ...(isSelected 
                                                                                    ? { background: '#f0fdf4', color: '#166534', borderColor: '#16a34a' }
                                                                                    : { background: '#fff', color: '#64748b', borderColor: '#cbd5e1' })
                                                                            }}
                                                                        >
                                                                            {niv.value} - {niv.label}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>

                                                        {/* Commentaire */}
                                                        <div>
                                                            <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Justification / Exemple</div>
                                                            <textarea
                                                                placeholder="Illustrez votre note par un exemple concret (optionnel)..."
                                                                value={peerComments[skill.skillId] || ""}
                                                                onChange={e => handleComment(skill.skillId, e.target.value)}
                                                                rows={2}
                                                                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', fontFamily: 'inherit', resize: 'vertical' }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
